import { formatDateBR } from '@/lib/format';
import type { ChipTone, DetailFieldMap, UserRef } from './types';

// ─── Helpers comuns aos fieldMaps de cada dominio ────────────────────────

export function asStr(v: unknown): string | null {
  return v != null && v !== '' ? String(v) : null;
}
export function asRaw(v: unknown): string | number | null {
  return v == null || v === '' ? null : typeof v === 'number' ? v : String(v);
}
export function fmtDate(v: unknown): string | null {
  return v ? formatDateBR(String(v)) : null;
}
export function fmtNum(v: unknown, suffix = ''): string | null {
  return v != null && v !== ''
    ? `${Number(v).toLocaleString('pt-BR')}${suffix}`
    : null;
}
export function fmtBRL(v: unknown): string | null {
  return v != null && v !== ''
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;
}

export function labelFromOptions(
  v: unknown,
  opts: Array<{ value: string; label: string }>,
): string | null {
  if (v == null || v === '') return null;
  const found = opts.find((o) => o.value === String(v));
  return found ? found.label : String(v);
}

export function parseResponsaveis(raw: unknown): UserRef[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r): UserRef | null => {
      if (typeof r === 'string') return null;
      if (r && typeof r === 'object' && 'id' in r) {
        const obj = r as Record<string, unknown>;
        return {
          id: Number(obj.id),
          name: typeof obj.name === 'string' ? obj.name : null,
          email: typeof obj.email === 'string' ? obj.email : null,
        };
      }
      return null;
    })
    .filter((u): u is UserRef => u != null && Number.isFinite(u.id));
}

const STATUS_LABEL: Record<string, string> = {
  em_andamento: 'em andamento',
  em_analise: 'em análise',
  protocolado: 'protocolado',
  concluido: 'concluído',
  cancelado: 'cancelado',
  suspenso: 'suspenso',
  aguardando_incra: 'aguardando INCRA',
  aceito: 'aceito',
  rejeitado: 'rejeitado',
  aprovado: 'aprovado',
  contratado: 'contratado',
  perdido: 'perdido',
  arquivado: 'arquivado',
  aguardando: 'aguardando',
};

export function statusLabel(s: string | null | undefined): string | null {
  if (!s) return null;
  return STATUS_LABEL[s] ?? s.replace(/_/g, ' ');
}

export function statusTone(s: string | null | undefined): ChipTone {
  switch (s) {
    case 'concluido':
    case 'aceito':
    case 'aprovado':
    case 'contratado':
      return 'ok';
    case 'em_analise':
    case 'protocolado':
    case 'aguardando_incra':
    case 'aguardando':
      return 'info';
    case 'em_andamento':
      return 'accent';
    case 'cancelado':
    case 'suspenso':
    case 'rejeitado':
    case 'perdido':
      return 'danger';
    case 'arquivado':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export interface PhaseField {
  key: string;
  label: string;
  helpText?: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'boolean'
    | 'select'
    | 'triagem_aceite'
    | 'emitir_orcamento'
    | 'emitir_formulario_prioridade'
    | 'emitir_formulario_pra'
    | 'emitir_formulario_alteracao_email_sicar'
    | 'emitir_formulario_cancelamento_car'
    | 'emitir_prada_simplificado';
  options?: Array<{ value: string; label: string }>;
  editable?: boolean;
  showIf?: (c: Record<string, unknown>) => boolean;
}

export interface PhaseDef {
  name: string;
  fields: PhaseField[];
}

export interface PipeSpec {
  colStart: number;
  phases: PhaseDef[];
}

export function displayFor(f: PhaseField, value: unknown): string {
  if (f.type === 'triagem_aceite') return asStr(value) ?? '—';
  if (f.type === 'emitir_orcamento') return asStr(value) ?? 'Emitir proposta em PDF';
  if (f.type === 'emitir_formulario_prioridade') return 'Emitir formulário';
  if (f.type === 'emitir_formulario_pra') return 'Emitir formulário';
  if (f.type === 'emitir_formulario_alteracao_email_sicar') return 'Emitir formulário';
  if (f.type === 'emitir_formulario_cancelamento_car') return 'Emitir formulário';
  if (f.type === 'emitir_prada_simplificado') return 'Emitir PRADA simplificado';
  if (f.type === 'date') return fmtDate(value) ?? '—';
  if (f.type === 'select' && f.options)
    return labelFromOptions(value, f.options) ?? '—';
  if (f.type === 'boolean') {
    if (value === true || value === 'true' || value === 't') return 'Sim';
    if (value === false || value === 'false' || value === 'f') return 'Não';
    return '—';
  }
  if (f.type === 'number') {
    if (/valor|honorario|custas|emolumentos|aliquota|taxa|bonus|percentual/i.test(f.key))
      return fmtBRL(value) ?? fmtNum(value) ?? '—';
    if (/area/i.test(f.key)) return fmtNum(value, ' ha') ?? '—';
    if (/perimetro/i.test(f.key)) return fmtNum(value, ' m') ?? '—';
    return fmtNum(value) ?? '—';
  }
  return asStr(value) ?? '—';
}

// Constrói os campos da seção "Dados principais" (CPF + Imóvel + Matrícula
// + Cartório + Responsáveis + Previsão de conclusão). Imóvel/Matrícula/
// Cartório read-only, Previsão de conclusão editável.
export function buildDadosPrincipais(c: Record<string, unknown>): ReturnType<DetailFieldMap> {
  const out: ReturnType<DetailFieldMap> = [];
  const responsaveisList = parseResponsaveis(c.responsaveis);
  out.push({
    section: 'Dados principais',
    label: 'CPF',
    value: asStr(c.cliente_cpf) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Imóvel',
    value: asStr(c.imovel_denominacao) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Matrícula',
    value: asStr(c.imovel_matricula) ?? '—',
    alwaysShow: true,
  });
  if (asStr(c.cartorio)) {
    out.push({
      section: 'Dados principais',
      label: 'Cartório',
      value: String(c.cartorio),
      alwaysShow: true,
    });
  }
  out.push({
    section: 'Dados principais',
    label: 'Responsável(is)',
    value: '',
    alwaysShow: true,
    type: 'responsaveis',
    responsaveis: responsaveisList,
  });
  out.push({
    section: 'Dados principais',
    label: 'Previsão de conclusão',
    value: fmtDate(c.data_previsao_conclusao) ?? '—',
    alwaysShow: true,
    field: 'data_previsao_conclusao',
    type: 'date',
    rawValue: asRaw(c.data_previsao_conclusao),
  });
  return out;
}

// Constrói as seções de fases. Past phases read-only, current editable.
// alwaysShow=true em tudo — fases passadas sem dados preenchidos ainda
// aparecem (com '—'), o que mantém o card colapsável visível pra navegação.
export function buildPhases(
  c: Record<string, unknown>,
  pipe: PipeSpec,
): ReturnType<DetailFieldMap> {
  const out: ReturnType<DetailFieldMap> = [];
  const ordemNum = Number(c.kanban_ordem) || 0;
  const colNum = Number(c.kanban_coluna) || 0;
  if (!ordemNum && colNum < pipe.colStart) return out;
  const phaseIndex = ordemNum || (colNum - pipe.colStart + 1);
  const visibleCount = Math.min(phaseIndex, pipe.phases.length);
  for (let i = 1; i <= visibleCount; i++) {
    const phase = pipe.phases[i - 1];
    if (!phase) continue;
    const sectionName = `${i}. ${phase.name}`;
    const editable = i === phaseIndex;
    for (const f of phase.fields) {
      if (f.showIf && !f.showIf(c)) continue;
      const item: ReturnType<DetailFieldMap>[number] = {
        section: sectionName,
        label: f.label,
        value: displayFor(f, c[f.key]),
        alwaysShow: true,
        rawValue: asRaw(c[f.key]),
        type: f.type,
      };
      if (f.helpText) item.helpText = f.helpText;
      if (editable && f.editable !== false) {
        item.field = f.key;
        if (f.options) item.options = f.options;
      } else if (f.options) {
        item.options = f.options;
      }
      out.push(item);
    }
  }
  return out;
}

// Constrói a seção "Observações gerais" — sempre presente, sempre editável.
export function buildObservacoes(c: Record<string, unknown>): ReturnType<DetailFieldMap> {
  const raw = asStr(c.observacoes) ?? '';
  const isTriagemCredito = String(c.tipo_processo || '') === 'triagem_credito';
  const value = isTriagemCredito
    ? raw.replace(/\s*\n+\s*/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 600)
    : raw;

  return [
    {
      section: 'Observações gerais',
      label: 'Observações',
      value,
      alwaysShow: true,
      field: 'observacoes',
      type: 'textarea',
      rawValue: isTriagemCredito ? value : asRaw(c.observacoes),
    },
  ];
}
